import { useState, useEffect, useMemo, useCallback } from 'react'
import { FootballMatch, GroupedMatches } from '@/lib/football-api'

interface UseAllMatchesOptions {
  date: string
  league?: string | number
  autoRefresh?: boolean
  refreshInterval?: number
}

interface UseAllMatchesReturn {
  upcoming: FootballMatch[]
  live: FootballMatch[]
  finished: FootballMatch[]
  allMatches: FootballMatch[]
  groupedUpcoming: GroupedMatches
  groupedLive: GroupedMatches
  groupedFinished: GroupedMatches
  groupedAll: GroupedMatches
  loading: boolean
  error: string | null
  refetch: () => void
}

export function useAllMatches(options: UseAllMatchesOptions): UseAllMatchesReturn {
  const { date, league = 'all', autoRefresh = false, refreshInterval = 300000 } = options // 5 minutes (increased from 1 minute)
  
  const [upcoming, setUpcoming] = useState<FootballMatch[]>([])
  const [live, setLive] = useState<FootballMatch[]>([])
  const [finished, setFinished] = useState<FootballMatch[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchMatches = useCallback(async () => {
    setLoading(true)
    setError(null)
    
    try {
      const response = await fetch(`/api/football/all-matches?date=${date}&league=${league}`)
      
      if (!response.ok) {
        throw new Error(`Failed to fetch matches: ${response.status}`)
      }
      
      const result = await response.json()
      
      // Check if API returned error
      if (result.error) {
        throw new Error(result.error)
      }
      
      // Extract data from API response
      const data = result.data || {}
      
      setUpcoming(data.upcoming || [])
      setLive(data.live || [])
      setFinished(data.finished || [])
      
      // Debug log if no matches found
      if (!data.upcoming?.length && !data.live?.length && !data.finished?.length) {
        if (result.debug) {
          // Keep only this debug log as it might be needed for troubleshooting
        }
      }
    } catch (err) {
      console.error('[useAllMatches] Error:', err)
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [date, league])

  // Group matches by competition
  const groupedUpcoming = useMemo(() => {
    const grouped: GroupedMatches = {}
    
    upcoming.forEach(match => {
      const competitionId = match.competition_id?.toString() || 'unknown'
      
      if (!grouped[competitionId]) {
        grouped[competitionId] = {
          id: match.competition_id || 0,
          name: match.competition_name || 'Unknown Competition',
          logo: match.competition_emblem || '',
          priority: match.priority || 999,
          matches: []
        }
      }
      
      grouped[competitionId].matches.push(match)
    })
    
    return grouped
  }, [upcoming])

  const groupedLive = useMemo(() => {
    const grouped: GroupedMatches = {}
    
    live.forEach(match => {
      const competitionId = match.competition_id?.toString() || 'unknown'
      
      if (!grouped[competitionId]) {
        grouped[competitionId] = {
          id: match.competition_id || 0,
          name: match.competition_name || 'Unknown Competition',
          logo: match.competition_emblem || '',
          priority: match.priority || 999,
          matches: []
        }
      }
      
      grouped[competitionId].matches.push(match)
    })
    
    return grouped
  }, [live])

  // Group finished matches by competition
  const groupedFinished = useMemo(() => {
    const grouped: GroupedMatches = {}
    
    finished.forEach(match => {
      const competitionId = match.competition_id?.toString() || 'unknown'
      
      if (!grouped[competitionId]) {
        grouped[competitionId] = {
          id: match.competition_id || 0,
          name: match.competition_name || 'Unknown Competition',
          logo: match.competition_emblem || '',
          priority: match.priority || 999,
          matches: []
        }
      }
      
      grouped[competitionId].matches.push(match)
    })
    
    return grouped
  }, [finished])

  // Combine all matches for schedule page
  const allMatches = useMemo(() => {
    return [...live, ...upcoming, ...finished]
  }, [live, upcoming, finished])

  // Combine grouped matches for schedule page
  const groupedAll = useMemo(() => {
    const combined = { ...groupedUpcoming, ...groupedFinished }
    
    // Add live matches to grouped data
    Object.keys(groupedLive).forEach(competitionId => {
      if (combined[competitionId]) {
        combined[competitionId].matches = [
          ...groupedLive[competitionId].matches,
          ...combined[competitionId].matches
        ]
      } else {
        combined[competitionId] = groupedLive[competitionId]
      }
    })
    
    return combined
  }, [groupedUpcoming, groupedLive, groupedFinished])

  // Initial fetch
  useEffect(() => {
    fetchMatches()
  }, [fetchMatches])

  // Auto refresh
  useEffect(() => {
    if (!autoRefresh) return

    const interval = setInterval(() => {
      fetchMatches()
    }, refreshInterval)

    return () => clearInterval(interval)
  }, [autoRefresh, refreshInterval, fetchMatches])

  return {
    upcoming,
    live,
    finished,
    allMatches,
    groupedUpcoming,
    groupedLive,
    groupedFinished,
    groupedAll,
    loading,
    error,
    refetch: fetchMatches
  }
} 